import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Bell,
  Check,
  CheckCircle,
  Copy,
  Loader2,
  Plus,
  Power,
  TestTube2,
  Trash2,
  XCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import {
  useCreateWebhook,
  useDeleteWebhook,
  useTestWebhook,
  useUpdateWebhook,
  useWebhookSecret,
  useWebhooks,
  WEBHOOK_EVENTS,
  type Webhook,
} from '@/api/integrations'

const schema = z.object({
  name: z.string().trim().min(1, 'name_required'),
  url: z.string().trim().url('invalid_url'),
  events: z.array(z.string()).min(1, 'events_required'),
})
type FormValues = z.infer<typeof schema>

const DEFAULTS: FormValues = { name: '', url: '', events: [] }

export function WebhooksTab() {
  const { t } = useTranslation()
  const { data: webhooks = [], isLoading } = useWebhooks()
  const createWebhook = useCreateWebhook()
  const updateWebhook = useUpdateWebhook()
  const deleteWebhook = useDeleteWebhook()
  const testWebhook = useTestWebhook()
  // ?secret=<id> : un lien envoyé au client ouvre directement le secret de ce webhook.
  const [searchParams] = useSearchParams()
  const secretDemande = Number(searchParams.get('secret')) || null

  const [showForm, setShowForm] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<Webhook | null>(null)
  const [testResult, setTestResult] = useState<{
    id: number
    success: boolean
    message: string
    detail?: string
    hint?: string
  } | null>(null)
  const [testingId, setTestingId] = useState<number | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  })
  const { control, register, setValue, handleSubmit, formState, reset } = form
  const events = useWatch({ control, name: 'events' })

  const toggleEvent = (event: string) => {
    setValue(
      'events',
      events.includes(event)
        ? events.filter((e) => e !== event)
        : [...events, event],
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const handleCreate = async (values: FormValues) => {
    try {
      await createWebhook.mutateAsync(values)
      toast.success(t('manager.integrations.webhooks.toast.created'))
      reset(DEFAULTS)
      setShowForm(false)
    } catch {
      toast.error(t('manager.integrations.webhooks.toast.createError'))
    }
  }

  const handleToggle = async (wh: Webhook) => {
    try {
      await updateWebhook.mutateAsync({
        id: wh.id,
        patch: { is_active: !wh.is_active },
      })
    } catch {
      toast.error(t('manager.integrations.webhooks.toast.toggleError'))
    }
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteWebhook.mutateAsync(pendingDelete.id)
      toast.success(t('manager.integrations.webhooks.toast.deleted'))
    } catch {
      toast.error(t('manager.integrations.webhooks.toast.deleteError'))
    } finally {
      setPendingDelete(null)
    }
  }

  const handleTest = async (wh: Webhook) => {
    setTestingId(wh.id)
    setTestResult(null)
    let success = false
    try {
      // The endpoint's own answer, so the user knows where a failure comes from.
      const res = await testWebhook.mutateAsync(wh.id)
      success = res.success
      const key = res.reason === 'url_refused' ? 'urlRefused' : res.reason
      setTestResult({
        id: wh.id,
        success: res.success,
        message: t(`manager.integrations.webhooks.testResult.${key}`, { code: res.http_status ?? '' }),
        detail: res.reason === 'http' ? res.detail : undefined,
        hint:
          res.http_status === 401 || res.http_status === 403
            ? t('manager.integrations.webhooks.testResult.authHint')
            : undefined,
      })
    } catch {
      setTestResult({ id: wh.id, success: false, message: t('manager.integrations.webhooks.toast.testError') })
    } finally {
      setTestingId(null)
      // A failure stays long enough to read it.
      setTimeout(() => setTestResult((r) => (r?.id === wh.id ? null : r)), success ? 5000 : 20000)
    }
  }

  const errMsg = (key?: string) =>
    key ? t(`manager.integrations.errors.${key}`) : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {t('manager.integrations.webhooks.title')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('manager.integrations.webhooks.description')}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('manager.integrations.webhooks.create')}
        </Button>
      </div>

      {/* Create form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit(handleCreate)}
            className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4"
          >
            <div>
              <Label htmlFor="wh-name">
                {t('manager.integrations.webhooks.nameLabel')}
              </Label>
              <Input
                id="wh-name"
                placeholder={t('manager.integrations.webhooks.namePlaceholder')}
                className="mt-1"
                {...register('name')}
              />
              {formState.errors.name && (
                <p className="text-xs text-red-500 mt-1">
                  {errMsg(formState.errors.name.message)}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="wh-url">
                {t('manager.integrations.webhooks.urlLabel')}
              </Label>
              <Input
                id="wh-url"
                type="url"
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                className="mt-1 font-mono"
                {...register('url')}
              />
              {formState.errors.url && (
                <p className="text-xs text-red-500 mt-1">
                  {errMsg(formState.errors.url.message)}
                </p>
              )}
            </div>

            <div>
              <Label className="block mb-2">
                {t('manager.integrations.webhooks.eventsLabel')}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {WEBHOOK_EVENTS.map((value) => {
                  const selected = events.includes(value)
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleEvent(value)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors',
                        selected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300'
                          : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50',
                      )}
                    >
                      {t(
                        `manager.integrations.webhooks.events.${value.replace('.', '_')}`,
                      )}
                    </button>
                  )
                })}
              </div>
              {formState.errors.events && (
                <p className="text-xs text-red-500 mt-1">
                  {errMsg(formState.errors.events.message)}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={createWebhook.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {createWebhook.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {t('manager.integrations.webhooks.save')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowForm(false)
                  reset(DEFAULTS)
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Webhooks list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : webhooks.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          <Bell className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">
            {t('manager.integrations.webhooks.empty')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((wh) => (
            <div
              key={wh.id}
              id={`webhook-${wh.id}`}
              className={cn(
                'rounded-xl border p-4 transition-colors bg-white dark:bg-slate-900',
                wh.is_active
                  ? 'border-slate-200 dark:border-slate-800'
                  : 'border-red-200 dark:border-red-500/30 opacity-60',
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-4 min-w-0">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                      wh.is_active
                        ? 'bg-purple-100 dark:bg-purple-500/20'
                        : 'bg-slate-100 dark:bg-slate-800',
                    )}
                  >
                    <Bell
                      className={cn(
                        'w-5 h-5',
                        wh.is_active ? 'text-purple-600' : 'text-slate-400',
                      )}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">
                      {wh.name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-mono mt-0.5 break-all">
                      {wh.url.length > 60 ? wh.url.slice(0, 60) + '…' : wh.url}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {wh.events.map((evt) => (
                        <span
                          key={evt}
                          className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded text-xs"
                        >
                          {t(
                            `manager.integrations.webhooks.events.${evt.replace('.', '_')}`,
                            { defaultValue: wh.events_display?.[evt] || evt },
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleTest(wh)}
                    disabled={testingId === wh.id}
                  >
                    {testingId === wh.id ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <TestTube2 className="w-4 h-4 mr-1.5" />
                    )}
                    {t('manager.integrations.webhooks.test')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleToggle(wh)}
                    disabled={updateWebhook.isPending}
                  >
                    <Power className="w-4 h-4 mr-1.5" />
                    {wh.is_active
                      ? t('manager.integrations.webhooks.deactivate')
                      : t('manager.integrations.webhooks.activate')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingDelete(wh)}
                    className="text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" />
                    {t('manager.integrations.webhooks.delete')}
                  </Button>
                </div>
              </div>

              {testResult?.id === wh.id && (
                <div className="mt-3 space-y-1">
                  <div
                    className={cn(
                      'flex items-center gap-2 text-sm',
                      testResult.success ? 'text-green-600' : 'text-red-600',
                    )}
                  >
                    {testResult.success ? (
                      <CheckCircle className="w-4 h-4 shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 shrink-0" />
                    )}
                    {testResult.message}
                  </div>
                  {testResult.detail && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 pl-6">
                      {t('manager.integrations.webhooks.testResult.answer')}{' '}
                      <code className="font-mono text-slate-700 dark:text-slate-200 break-all">{testResult.detail}</code>
                    </p>
                  )}
                  {testResult.hint && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 pl-6">{testResult.hint}</p>
                  )}
                </div>
              )}

              <SigningSecret webhookId={wh.id} ouvrir={secretDemande === wh.id} />

              {wh.failure_count > 0 && (
                <p className="mt-2 text-xs text-red-500">
                  {t('manager.integrations.webhooks.failures', {
                    count: wh.failure_count,
                  })}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('manager.integrations.webhooks.deleteDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('manager.integrations.webhooks.deleteDialog.description', {
                name: pendingDelete?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteWebhook.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleteWebhook.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t('manager.integrations.webhooks.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** The signing secret of one webhook: hidden until asked for, then copied in one click. */
function SigningSecret({ webhookId, ouvrir = false }: { webhookId: number; ouvrir?: boolean }) {
  const { t } = useTranslation()
  const getSecret = useWebhookSecret()
  const [secret, setSecret] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const dejaOuvert = useRef(false)

  const show = async () => {
    try {
      setSecret(await getSecret.mutateAsync(webhookId))
    } catch {
      toast.error(t('manager.integrations.webhooks.secret.error'))
    }
  }
  // Opened from a direct link: bring the webhook into view and show its secret once.
  useEffect(() => {
    if (!ouvrir || dejaOuvert.current) return
    dejaOuvert.current = true
    document.getElementById(`webhook-${webhookId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    void show()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvrir, webhookId])

  const copy = async () => {
    if (!secret) return
    try {
      await navigator.clipboard.writeText(secret)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('manager.integrations.webhooks.secret.copyError'))
    }
  }

  // A plain field of the card, masked by default, like any provider's signing secret.
  const lien = 'font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50'
  return (
    <div className="mt-3 text-xs">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-slate-500 dark:text-slate-400">{t('manager.integrations.webhooks.secret.label')}</span>
        {secret ? (
          <>
            {/* Never filmed by session replay nor shown during a screen share. */}
            <code data-masque className="ph-no-capture font-mono text-slate-800 dark:text-slate-100 break-all">
              {secret}
            </code>
            <button type="button" onClick={() => void copy()} className={cn(lien, 'inline-flex items-center gap-1')}>
              {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
              {copied ? t('manager.integrations.webhooks.secret.copied') : t('manager.integrations.webhooks.secret.copy')}
            </button>
            <button type="button" onClick={() => setSecret(null)} className={lien}>
              {t('manager.integrations.webhooks.secret.hide')}
            </button>
          </>
        ) : (
          <>
            <code className="font-mono text-slate-400 dark:text-slate-500 tracking-widest">••••••••••••••••</code>
            <button type="button" onClick={() => void show()} disabled={getSecret.isPending} className={cn(lien, 'inline-flex items-center gap-1')}>
              {getSecret.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
              {t('manager.integrations.webhooks.secret.show')}
            </button>
          </>
        )}
      </div>
      {secret && <p className="mt-1 text-slate-500 dark:text-slate-400">{t('manager.integrations.webhooks.secret.help')}</p>}
    </div>
  )
}
